import { useCallback, useEffect, useState } from 'react';
import { taskApi, type Task, type NewTask } from '../../api';
import Select from '../common/Select';
import CompleteTaskModal from '../common/CompleteTaskModal';
import EditTaskModal from '../common/EditTaskModal';

type Props = { matterId: number | string };

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '',          label: 'All' },
  { value: 'open',      label: 'Open' },
  { value: 'completed', label: 'Completed' },
];

const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

export default function TasksPanel({ matterId }: Props) {
  const [tasks, setTasks] = useState<Task[]>([]);
  // Default to ALL so completed tasks are visible after the user finishes them.
  // Filter sticks in component state so users can switch to Open while triaging.
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // The task we're completing — if billable, we open the time-entry prompt
  // first; if not, we complete immediately. This mirrors the Tasks page.
  const [completingTask, setCompletingTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await taskApi.list({ matter_id: matterId, status: status || undefined });
      setTasks(r.tasks || []);
    } finally { setLoading(false); }
  }, [matterId, status]);

  useEffect(() => { load(); }, [load]);

  const asBool = (v: Task['billable']) => v === true || v === 'true';

  const onCompleteClick = async (t: Task) => {
    if (asBool(t.billable)) {
      setCompletingTask(t); // open the time-entry modal
      return;
    }
    setBusy(String(t.ROWID));
    try { await taskApi.complete(t.ROWID); await load(); }
    finally { setBusy(null); }
  };

  const onDeleteClick = async (t: Task) => {
    if (!confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    setBusy(String(t.ROWID));
    try { await taskApi.remove(t.ROWID); await load(); }
    finally { setBusy(null); }
  };

  return (
    <div className="stack">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>Tasks</h3>
        <Select
          value={status}
          onChange={setStatus}
          style={{ width: 160 }}
          options={STATUS_FILTERS}
        />
        <button className="primary" onClick={() => setAdding(true)}>+ New task</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : tasks.length === 0 ? (
          <div style={{ padding: 18 }} className="muted">
            No {status || ''} tasks. Tasks auto-create when you advance the matter through procedural stages — or add one manually above.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {tasks.map((t) => (
              <li key={String(t.ROWID)} style={row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, textDecoration: t.status === 'completed' ? 'line-through' : 'none' }}>
                    {t.name}
                  </div>
                  {t.description && (
                    <div className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t.description}
                    </div>
                  )}
                </div>
                <span className={`pill ${t.priority === 'urgent' || t.priority === 'high' ? 'danger' : ''}`}>
                  {t.priority}
                </span>
                <span className="muted" style={{ fontSize: 12, minWidth: 90 }}>
                  {t.due_date || '—'}
                </span>
                <span className="pill">{t.status.replace('_', ' ')}</span>
                {!asBool(t.billable) && <span className="pill" style={{ background: 'var(--color-info-soft)', color: 'var(--color-info)' }}>non-billable</span>}
                <button
                  className="ghost"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={() => setEditingTask(t)}
                  disabled={busy === String(t.ROWID)}
                >
                  Edit
                </button>
                <button
                  className="ghost"
                  style={{ padding: '4px 10px', fontSize: 12, color: 'var(--color-danger)' }}
                  onClick={() => onDeleteClick(t)}
                  disabled={busy === String(t.ROWID)}
                >
                  Delete
                </button>
                {t.status !== 'completed' && t.status !== 'cancelled' && (
                  <button
                    className="primary"
                    style={{ padding: '6px 12px', fontSize: 13 }}
                    onClick={() => onCompleteClick(t)}
                    disabled={busy === String(t.ROWID)}
                  >
                    {busy === String(t.ROWID) ? '…' : 'Complete'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {adding && (
        <NewTaskModal
          matterId={matterId}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      )}

      {completingTask && (
        <CompleteTaskModal
          task={completingTask}
          onClose={() => setCompletingTask(null)}
          onCompleted={() => { setCompletingTask(null); load(); }}
        />
      )}

      {editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSaved={() => { setEditingTask(null); load(); }}
        />
      )}
    </div>
  );
}

function NewTaskModal({ matterId, onClose, onSaved }: { matterId: number | string; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<NewTask>({
    matter_id: matterId,
    name: '',
    description: '',
    priority: 'normal',
    due_date: '',
    billable: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await taskApi.create({
        ...form,
        description: form.description || undefined,
        due_date: form.due_date || undefined,
      });
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>New task</h3>
        <div className="stack">
          <div>
            <label>Name</label>
            <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label>Description (optional)</label>
            <textarea rows={3} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="row">
            <div className="grow">
              <label>Priority</label>
              <Select
                value={form.priority || 'normal'}
                onChange={(v) => setForm({ ...form, priority: v as NewTask['priority'] })}
                options={PRIORITIES.map((p) => ({ value: p, label: p }))}
              />
            </div>
            <div className="grow">
              <label>Due date</label>
              <input type="date" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 0, fontSize: 13, color: 'var(--color-text)' }}>
            <input
              type="checkbox"
              checked={form.billable !== false}
              onChange={(e) => setForm({ ...form, billable: e.target.checked })}
              style={{ width: 'auto' }}
            />
            <span>Billable</span>
          </label>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save task'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 18px', borderBottom: '1px solid var(--color-border)',
};

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 520, maxWidth: '90vw' };
